// Read Only Flag
export const IS_READ_ONLY = true;

// URL's
export const BASENAME = '/ECMv6';
export const AAG_SOD_BASENAME = '/soddetail';
export const AAG_SOD_VIOLATIONS = `${AAG_SOD_BASENAME}/violations`;
export const AAG_SOD_TIME = `${AAG_SOD_BASENAME}/time`;
export const AAG_SOD_USER = `${AAG_SOD_BASENAME}/user`;
export const AAG_SOD_ENTITLEMENT = `${AAG_SOD_BASENAME}/entitlement`;
export const AAG_SOD_ROLE = `${AAG_SOD_BASENAME}/role`;
export const AAG_SOD_DETAILS = `${AAG_SOD_VIOLATIONS}/details`;
export const AAG_SOD_DETAILS_WITH_ID = `${AAG_SOD_DETAILS}/:qId`;
export const AAG_SOD_RECOMMENDATION = `${AAG_SOD_VIOLATIONS}/recommendations`;
export const AAG_SOD_RECOMMENDATION_WITH_ID = `${AAG_SOD_RECOMMENDATION}/:qId`;
export const AAG_SOD_REPORT = `${AAG_SOD_VIOLATIONS}/report`;

// Pages and tabs
interface ViolationsTab {
  label: string;
  icon: string;
  value: string;
  isActive: boolean;
}

export const violationsHome = AAG_SOD_VIOLATIONS;
export const violationsTime = AAG_SOD_TIME;
export const violationsUser = AAG_SOD_USER;
export const violationsEntitlement = AAG_SOD_ENTITLEMENT;
export const violationsRole = AAG_SOD_ROLE;
export const activeViolationsTab = '';

// Making the tabs `isActive` flag will play with routes also
export const VIOLATIONS_PAGE_TABS: ViolationsTab[] = [
  {
    label: 'Violations',
    icon: 'SOD',
    value: violationsHome,
    isActive: true,
  },
  {
    label: 'Time',
    icon: 'Time',
    value: violationsTime,
    isActive: false,
  },
  {
    label: 'User',
    icon: 'User',
    value: violationsUser,
    isActive: false,
  },
  {
    label: 'Entitlement',
    icon: 'Entitlement',
    value: violationsEntitlement,
    isActive: true,
  },
  {
    label: 'Role',
    icon: 'Role',
    value: violationsRole,
    isActive: false,
  },
] as const;

export const DISABLED_PAGES: string[] = [violationsEntitlement] as const;

export const AAG_ACTIVE_TABS = VIOLATIONS_PAGE_TABS.filter(
  (page) => page.isActive && !DISABLED_PAGES.includes(page.value)
).map((page) => page.value);

export const AAG_HEADER_URLS = [
  ...AAG_ACTIVE_TABS,
  AAG_SOD_DETAILS,
  AAG_SOD_DETAILS_WITH_ID,
  AAG_SOD_RECOMMENDATION,
];

// Supported Themes
export type DataThemeOptions = 'light' | 'dark';

export const SYSTEM_DATA_THEMES: Record<'LIGHT' | 'DARK', DataThemeOptions> = {
  LIGHT: 'light',
  DARK: 'dark',
};

export interface EntitlementRowType {
  id: number;
  title: string;
  role: string;
  applicationComp: React.ReactNode;
  description: string;
  inherentRisks: number;
  users: number;
  violations: number;
  entitlementType: string;
  soxCritical: string;
  sysCritical: string;
  risk: string;
  privileged: string;
  financial: string;
  updateDate: string;
}

export const SEARCH_TEXT_LENGTH = 3;

// Shorthand for the color tokens to reduce code clutter
export const CF_PRI_MED = '--color-foreground-primary-medium';
export const CF_SEC_BLD = '--color-foreground-secondary-bold';
export const CF_SEC_MED = '--color-foreground-secondary-medium';
export const CF_NEU_BLD = '--color-foreground-neutral-bold';
export const CF_NEU_MED = '--color-foreground-neutral-medium';
export const CF_NEU_STL = '--color-foreground-neutral-subtle';
export const CF_NEU_INV = '--color-foreground-neutral-inverse';
export const CF_CRT_BLD = '--color-foreground-critical-bold';
export const CF_CRT_MED = '--color-foreground-critical-medium';
export const CF_WRN_BLD = '--color-foreground-warning-bold';
export const CF_WRN_STL = '--color-foreground-warning-subtle';
export const CF_INF_MED = '--color-foreground-info-medium';

// Media Queries
/**
 * Provides a guide for UX design breakpoints and their corresponding naming structure.
 * This is a reference for responsive design, indicating the pixel width and associated
 * designations for various device sizes.
 *
 * Mobile:
 * - 360px: 140 MobileXSmall (This is below our minimum, but it’s a common Android size, adding it here just in case)
 * - 375px: 130 MobileSmall
 * - 480px: 120 MobileLarge
 * - 600px: 110 MobileXLarge
 *
 * Tablet:
 * - 768px: 100 TabletXSmall
 * - 834px: 90 TabletSmall
 * - 900px: 80 TabletMedium
 * - 960px: 70 TabletLarge
 * - 1024px: 60 TabletXLarge
 *
 * Desktop:
 * - 1280px: 50 DesktopSmall
 * - 1366px: 40 DesktopMedium
 * - 1440px: 30 DesktopLarge
 * - 1600px: 20 DesktopXLarge
 * - 1920px: 10 DesktopXXLarge
 */
export const SCREEN_SIZE_MOBILE_LARGE = 'MobileLarge';
export const SCREEN_SIZE_MOBILE_XLARGE = 'MobileXLarge';
export const SCREEN_SIZE_TABLET_XSMALL = 'TabletXSmall';
export const SCREEN_SIZE_TABLET_SMALL = 'TabletSmall';
export const SCREEN_SIZE_TABLET_MEDIUM = 'TabletMedium';
export const SCREEN_SIZE_TABLET_LARGE = 'TabletLarge';
export const SCREEN_SIZE_DESKTOP_SMALL = 'DesktopSmall';

export const SCREEN_WIDTH_QUERIES = [
  { query: '(max-width: 479px)', value: 120, label: SCREEN_SIZE_MOBILE_LARGE },
  { query: '(max-width: 599px)', value: 110, label: SCREEN_SIZE_MOBILE_XLARGE },
  { query: '(max-width: 767px)', value: 100, label: SCREEN_SIZE_TABLET_XSMALL },
  { query: '(max-width: 833px)', value: 90, label: SCREEN_SIZE_TABLET_SMALL },
  { query: '(max-width: 899px)', value: 80, label: SCREEN_SIZE_TABLET_MEDIUM },
  { query: '(max-width: 959px)', value: 80, label: SCREEN_SIZE_TABLET_LARGE },
  { query: '(max-width: 1279px)', value: 50, label: SCREEN_SIZE_DESKTOP_SMALL },
];

export const GLOBAL_ALERT_BANNER_TYPES = {
  CRITICAL: 'Critical',
  WARNING: 'Warning',
  SUCCESS: 'Success',
  INFO: 'Information',
};

// Advanced Filters
export const ABORT_ERROR = 'AbortError';
export const DEBOUNCE_TIME_FILTER_SEARCH = 400;

export const DEFAULT_AD_FILTER_PARAMS: {
  max: string;
  offset: string;
  search: string;
  key?: string;
} = {
  max: '500',
  offset: '0',
  search: '',
} as const;

export const ADVANCED_FILTERS_METHODS: Record<string, string> = {
  API: 'api_call_with_no_initial_options',
  API_WITH_OPTIONS: 'api_call_with_initial_options',
  SEARCH: 'api_call_with_search',
  FIXED: 'fixed_options',
  CUSTOM: 'custom_filters',
  INACTIVE: 'inactive_filters',
} as const;
